import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://bhgyvonfmvegmvzkthva.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJoZ3l2b25mbXZlZ212emt0aHZhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjAzODgzODIsImV4cCI6MjA3NTk2NDM4Mn0.Iq8ipOJM7hAg9nHCPBx62hwV3YVP2Dm-RUNCtusgNt8';

// Graceful handling: Log warning but don't throw in development (allow app to run with warnings)
let supabase: SupabaseClient;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase config missing! Check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env.local or Vercel env vars.');
  console.warn('Current values:', { supabaseUrl, supabaseAnonKey: supabaseAnonKey ? 'Set' : 'Missing' });
  console.warn('App will run without Supabase functionality. Set env vars to enable full features.');
  
  // In production, you might want to show a user-friendly error page instead
  if (import.meta.env.PROD) {
    throw new Error('Supabase configuration is required. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
  }
  
  // For dev, return a mock client or null (app should handle gracefully)
  // Note: This is a temporary workaround; proper config is still needed
  const mockClient: any = {
    auth: {
      signInWithPassword: async () => ({ error: { message: 'Supabase not configured' } }),
      signUp: async () => ({ error: { message: 'Supabase not configured' } }),
      signOut: async () => ({ error: { message: 'Supabase not configured' } }),
      getSession: async () => ({ data: { session: null } }),
      onAuthStateChange: (callback: any) => ({ data: { subscription: { unsubscribe: () => {} } } }),
      mfa: {
        enroll: async () => ({ error: { message: 'Supabase not configured' } }),
        verify: async () => ({ error: { message: 'Supabase not configured' } }),
        listFactors: async () => ({ totp: [] }),
        unenroll: async () => ({ error: { message: 'Supabase not configured' } }),
      },
      getUser: async () => ({ data: { user: null }, error: { message: 'Supabase not configured' } }),
    },
    from: (table: string) => ({
      select: async () => ({ data: [], error: { message: 'Supabase not configured' } }),
      insert: async () => ({ data: [], error: { message: 'Supabase not configured' } }),
      update: async () => ({ data: [], error: { message: 'Supabase not configured' } }),
      delete: async () => ({ data: [], error: { message: 'Supabase not configured' } }),
      eq: () => ({ single: async () => ({ data: null, error: { message: 'Supabase not configured' } }) }),
      rpc: async () => ({ data: [], error: { message: 'Supabase not configured' } }),
    }),
    channel: () => ({
      on: () => ({ subscribe: () => ({ data: { subscription: { unsubscribe: () => {} } } }) }),
      removeChannel: () => {},
    }),
    functions: {
      invoke: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
    },
    storage: {
      from: () => ({
        upload: async () => ({ data: null, error: { message: 'Supabase not configured' } }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
        remove: async () => ({ data: [], error: { message: 'Supabase not configured' } }),
      }),
    },
  };
  
  supabase = mockClient;
} else {
  // Create client only if vars are set
  try {
    supabase = createClient(supabaseUrl, supabaseAnonKey);
    // Optional: Test the connection asynchronously (no top-level await)
    // This test is now handled in a separate function to avoid build issues
    console.log('Supabase client initialized successfully.');
  } catch (e) {
    console.error('Failed to initialize Supabase client:', e);
    throw new Error('Failed to initialize Supabase client. Check your Supabase URL and Anon Key.');
  }
}

// NEW: Separate async function for connection testing (call this after client creation if needed)
export const testSupabaseConnection = async (): Promise<boolean> => {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Cannot test Supabase connection: config missing');
    return false;
  }

  try {
    // Test query to ensure connection works
    const { data, error } = await supabase.from('profiles').select('id').limit(1);
    
    if (error) {
      console.error('Supabase connection test failed:', error);
      return false;
    }

    console.log('Supabase connection test successful:', data);
    return true;
  } catch (error) {
    console.error('Supabase connection test error:', error);
    return false;
  }
};

export { supabase };